import { OverlayContainer } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, HostBinding, input, InputSignal, Signal, ViewChild } from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { IconComponent } from '@libs/components/icons';
import { NavItemModel } from '@libs/shared/models';
import { NavLinkDirective } from '@libs/shared/ui';

@Component({
  selector: 'ui-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavLinkDirective,
    MatMenu,
    MatMenuTrigger,
    IconComponent,
  ],
})
export class BreadcrumbComponent {
  @ViewChild(MatMenuTrigger)
  public menuTrigger?: MatMenuTrigger;

  public buttonHovered: boolean = false;
  public menuHovered: boolean = false;
  public title: InputSignal<string | undefined> = input();
  public nodes: InputSignal<NavItemModel[]> = input.required();

  // Home item is always the first element
  public homeItem: Signal<NavItemModel> = computed(() => {
    return this.nodes()[0];
  });

  // If there are more than 3 items - hide everything between first and last two items in a menu
  public menuItems: Signal<NavItemModel[]> = computed(() => {
    if (this.nodes().length > 3) {
      return this.nodes().slice(1, -2);
    }

    return [];
  });

  // Parent item only exists if there are more than two elements in array
  public parentItem: Signal<NavItemModel | undefined> = computed(() => {
    if (this.nodes().length > 2) {
      return this.nodes()[this.nodes().length - 2];
    }

    return undefined;
  });

  public currentItemTitle: Signal<string | undefined> = computed(() => {
    if (this.nodes().length < 2) {
      return undefined;
    }

    if (this.title()) {
      return this.title();
    }

    return this.nodes()[this.nodes().length - 1].label;
  });

  public constructor(
    private readonly overlayContainer: OverlayContainer,
  ) {
  }

  @HostBinding('class.empty')
  public get isEmpty(): boolean {
    return this.nodes().length === 0;
  }

  public buttonTouch(): void {
    this.menuTrigger?.toggleMenu();
  }

  public buttonEnter(): void {
    this.buttonHovered = true;
    this.menuTrigger?.openMenu();
    this.addMenuPanelListeners();
  }

  public buttonLeave(): void {
    this.buttonHovered = false;
    this.closeIfNothingHovered();
  }

  public menuOver(): void {
    this.menuHovered = true;
  }

  public menuLeave(): void {
    this.menuHovered = false;
    this.closeIfNothingHovered();
  }

  private closeIfNothingHovered(): void {
    setTimeout(() => {
      if (!this.menuHovered && !this.buttonHovered) {
        this.menuTrigger?.closeMenu();
      }
    }, 175);
  }

  private getMenuPanelContainer(): HTMLElement | null | undefined {
    return this.overlayContainer.getContainerElement().querySelector('.mat-mdc-menu-panel')?.parentElement;
  }

  private addMenuPanelListeners(): void {
    const panel: HTMLElement | null | undefined = this.getMenuPanelContainer();

    if (panel) {
      panel.onmouseover = this.menuOver.bind(this);
      panel.onmouseleave = this.menuLeave.bind(this);
    }
  }
}
